import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import DeckGL, { MapController } from "deck.gl";
import { MapView, WebMercatorViewport } from "@deck.gl/core";

import { renderAllLayers } from "./render_layers";

const YearMin = 1953;
const YearMax = 2026;
const Years = Array.from(
    { length: YearMax - YearMin + 2 },
    (_, i) => {
      return (i === 0)? "average" : String(YearMax - (i - 1));
    }
);

const JapanBounds = [
  [122.89305980289718, 23.977964821892858],
  [145.9483624174012, 45.64257950403767]
];

const App = () => {
  // 地図画面の大きさ
  const mapSize = () => {
    const topbar_height = 55;

    return {
      width: window.innerWidth,
      height: window.innerHeight - topbar_height
    };
  };

  // 日本全国fitzoneするときのViewState
  const fitViewState = () => {
    const {width, height} = mapSize();

    const viewport = new WebMercatorViewport({
      width,
      height,
    });
    const {longitude, latitude, zoom} = viewport.fitBounds(JapanBounds);

    return {
      width,
      height,
      longitude: longitude,
      latitude: latitude,
      zoom: zoom,
      minZoom: 3,
      maxZoom: 17,
      pitch: 45,
    };
  };

  // InitViewState
  const viewstateInit = fitViewState();
  const [viewState, setViewState] = useState(viewstateInit);

  // mapの繰り返し
  const views = useMemo(() => new MapView({ repeat: true }), []);

  // map移動時
  const onViewStateChange = ({viewState}) => {
    setViewState(viewState);
  };

  // ウィンドウリサイズ
  const handleResize = () => {
    const {width, height} = mapSize();

    setViewState((prev) => ({
      ...prev,
      width,
      height
    }));
  };
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // fitzoneHandler
  const fitzoneHandler = () => {
    setViewState(fitViewState());
  };

  // year
  const [year, setYear] = useState("average");
  const onChangeYear = ({target}) => {
    setYear(target.value);
  };

  // DateList
  const [dateList, setDateList] = useState([]);
  const [dateIndex, setDateIndex] = useState(0);
  const [datePlaying, setDatePlaying] = useState(false);

  // dateを自動再生
  useEffect(() => {
    if (!datePlaying) return;

    const id = window.setInterval(() => {
      setDateIndex((i) => {
        const next = i + 1;

        if (next >= dateList.length) {
          setDatePlaying(false); // 再生停止
          return i; // 最終値を維持
        }

        return next;
      });
    }, 1000);

    return () => window.clearInterval(id);
  }, [datePlaying]);

  // 自動再生ボタンが押された時の挙動
  const datePlayButtonHundler = () => {
    setDatePlaying((p) => !p);
  };

  // BloomList
  const [bloomList, setBloomList] = useState(null);

  // BloomListを作成
  const createBloomList = (bloom_list) => {
    setBloomList(
      bloom_list.map(v => {
        return {
          h3: v.h3,
          date_start: new Date(v.date_start),
          date_full: new Date(v.date_full),
          date_end: new Date(v.date_end),
        };
      })
    );
  };

  // Yearが変わった時に、jsonを取得してDateListとBloomListを設定する
  useEffect(() => {
    setDatePlaying(false);

    const url = `/data/${year}.json`;

    fetch(url)
      .then(res => {
        if (!res.ok) {
          return [];
        }
        return res.json();
      })
      .then(json => {
        createBloomList(json.bloom);

        const date_min = new Date(json.date_min);
        date_min.setDate(date_min.getDate() - 1);

        const date_max = new Date(json.date_max);
        date_max.setDate(date_max.getDate() + 1);

        const date_count = Math.floor((date_max - date_min) / (24 * 60 * 60 * 1000));

        setDateIndex((i) => {
          if (!dateList || dateList.length == 0) return 0;

          const date = dateList[i];
          date.setYear(date_min.getFullYear() + (dateList[dateList.length-1].getFullYear() - dateList[0].getFullYear()));

          if (date_min <= date && date <= date_max) {
            return Math.floor((date - date_min) / (24 * 60 * 60 * 1000));
          } else {
            return 0;
          }
        });

        setDateList(
          Array.from({ length: date_count + 1 }).map((_, d) => {
            const date = new Date(date_min);
            date.setDate(date.getDate() + d);

            return date;
          })
        );
      })
      .catch(() => {
        setBloomList(null);
        setDateIndex(0);
        setDateList([]);
      });

  }, [year]);

  // layer
  const layers = useMemo(() => {
    if (dateList.length === 0 || !bloomList) return [];

    const date = dateList[dateIndex];
    const bloom_start_list = bloomList.filter(({date_start, date_full}) => (date_start <= date && date < date_full));
    const bloom_full_list = bloomList.filter(({date_full, date_end}) => (date_full <= date && date < date_end));
    const bloom_end_list = bloomList.filter(({date_end}) => (date_end <= date));

    return renderAllLayers({bloomStartList: bloom_start_list, bloomFullList: bloom_full_list, bloomEndList: bloom_end_list, date: date});
  }, [bloomList, dateIndex]);

  return (
    <div className="App">
      <div className="map-container">
        <DeckGL
          layers={layers}
          initialViewState={viewstateInit}
          viewState={viewState}
          views={views}
          controller={{ type: MapController, dragRotate: false }}
          onViewStateChange={onViewStateChange}
          /* getCursor={() => cursor} */
          /* onHover={hoverMapHandler} */
          /* onClick={clickMapHandler} */
        />
      </div>

      <div className="map-attribution">
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noreferrer"
        >
          ©︎ OpenStreetMap contributors
        </a>
      </div>

      <div className="top-panel panel">
        {/* year-controls */}
        <div className="topbar-year-controls">
          <span className="label">Year</span>
          <select
            className="year-select"
            value={year}
            onChange={onChangeYear}
          >
            {
              Years.map(y => (
                <option key={"select-year-" + y} value={y}>
                  {y}
                </option>
              ))
            }
          </select>
        </div>

        {/* date-slider */}
        <div className="topbar-date-controls">
          <div className="date-group">
            <span className="label">Date</span>

            <div className="date-slider-wrapper">

              <span className="day-mark active">
                {
                  dateList[dateIndex] ? new Intl.DateTimeFormat(
                    "en-US",
                    { month: "short", day: "2-digit" }
                  ).format(dateList[dateIndex]) : ""
                }
              </span>

              <input
                type="range"
                className="date-slider"
                min={0}
                max={dateList.length - 1}
                step={1}
                value={dateIndex}
                onChange={(e) => setDateIndex(Number(e.target.value))}
                disabled={dateList.length === 0}
              />
            </div>

            <button
              className="date-play-button"
              type="button"
              title="Date Play"
              onClick={datePlayButtonHundler}
              disabled={dateList.length === 0}
            >
              {datePlaying ? "⏸" : "▶"}
            </button>
          </div>
        </div>

        {/* fit-zone */}
        <div className="topbar-fitzone-controls">
          <button
            className="fit-zone-button"
            type="button"
            title="Fit Zone"
            onClick={fitzoneHandler}
          >
            Fit Zone
          </button>
        </div>
      </div>

      <div className="legend-panel panel">
        <div className="legend-label">Bloom Starts</div>
        <div
          className="legend-color"
          style={{ background: "rgba(255,170,200,200)" }}
        />

        <div className="legend-label">Bloom Full</div>
        <div
          className="legend-color"
          style={{ background: "rgba(220,70,150,200)" }}
        />

        <div className="legend-label">Bloom Ends</div>
        <div
          className="legend-color"
          style={{ background: "rgba(120,190,70,200)" }}
        />

      </div>
    </div>
  );
};

export default App;
